"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import {
  StatsMetaline,
  type StatsMetalineItem,
} from "@/components/ui/stats-metaline"
import { Skeleton } from "@/components/ui/skeleton"
import { MyTasksWidget } from "@/components/dashboard/my-tasks-widget"
import { AgentPerformanceTable } from "@/components/dashboard/agent-performance-table"
import { LeaderboardWidget } from "@/components/dashboard/leaderboard-widget"
import { LeadsStatusChart } from "@/components/dashboard/leads-status-chart"
import { AppointmentsStatusChart } from "@/components/dashboard/appointments-status-chart"
import { useDashboardStats } from "@/components/dashboard/use-dashboard-stats"
import {
  UnassignedLeadsQueue,
  UNASSIGNED_QUEUE_LIMIT,
} from "@/components/dashboard/variants/team-leader/unassigned-leads-queue"
import { useDashboardWidgetFlags } from "@/components/dashboard/use-dashboard-widget-flags"
import { usePermissions } from "@/hooks/use-permissions"
import { cn } from "@/lib/utils"

import { getUnassignedLeads } from "@/lib/leads-api"
import { getLeaderboard, type LeaderboardResponse } from "@/lib/targets-api"
import type { Lead, PaginatedResponse } from "@/types"

/** Column count → static Tailwind class. Never interpolate into a class name. */
const WORK_GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
}

interface TeamData {
  /** One `/leaderboard` response, shared by the table AND the ranked strip. */
  leaderboard: LeaderboardResponse | null
  /** Paginated envelope — `meta.total` drives the queue's "View all N" footer. */
  unassigned: PaginatedResponse<Lead> | null
}

const EMPTY_TEAM_DATA: TeamData = { leaderboard: null, unassigned: null }

/**
 * Team-leader dashboard — "Team performance".
 *
 * Answers one question above the fold: how is the team doing, and what has not
 * been picked up yet. The shared headline band is replaced by a bespoke team
 * KPI strip (intake → unowned → quotes out → booked), then per-agent
 * performance as the hero, then the actionable unassigned queue beside the two
 * distribution charts.
 *
 * Permission contract (hide-not-disable): nothing is requested before
 * `usePermissions().isLoaded`, every call is skipped when its grant is missing,
 * and a widget whose data was never fetched is OMITTED along with its KPI tile
 * — so this variant degrades cleanly for a per-clinic custom role mapped onto
 * `team_leader` with a narrower grant set. The unassigned tile follows the
 * queue's `leads.view.all` gate rather than the looser stats gate: a viewer
 * scoped to `leads.view.assigned` can never see an unassigned lead, so the
 * count would be a misleading zero.
 *
 * NOTE (Teams feature): every number here is currently clinic-wide, because
 * that is what the endpoints return. When the Teams slice merges, the same
 * widgets become team-scoped server-side — no assumption about team membership
 * is encoded on this page, so nothing here needs to change beyond copy.
 */
export function TeamLeaderDashboard() {
  const t = useTranslations("dashboard")
  const { isLoaded, can } = usePermissions()
  const { showLeadsStatusChart, showAgentPerformance } = useDashboardWidgetFlags()

  // Headline sources (leads / appointments / quotes) — shared loader, already
  // permission-gated and hydration-safe.
  const {
    data: stats,
    isLoading: isStatsLoading,
    canViewLeads,
    canViewAppointments,
    canViewQuotes,
  } = useDashboardStats()

  // Team-specific sources this variant owns.
  const canViewTargets = can("targets.view")
  // The unassigned queue + KPI also need leads.view_unassigned (Smile 260922):
  // without it the backend ignores `?unassigned=1` and the queue would quietly
  // show every lead in the clinic.
  const canViewAllLeads = can("leads.view.all") && can("leads.view_unassigned")

  const [teamData, setTeamData] = useState<TeamData>(EMPTY_TEAM_DATA)
  const [isTeamLoading, setIsTeamLoading] = useState(true)

  useEffect(() => {
    // Hold both requests until the permission set has resolved — firing first
    // and discarding would 403 for users who legitimately lack the grant.
    if (!isLoaded) return

    let cancelled = false
    const loadTeamData = async () => {
      setIsTeamLoading(true)
      try {
        const [leaderboard, unassigned] = await Promise.all([
          canViewTargets ? getLeaderboard().catch(() => null) : Promise.resolve(null),
          canViewAllLeads
            ? getUnassignedLeads(UNASSIGNED_QUEUE_LIMIT).catch(() => null)
            : Promise.resolve(null),
        ])
        if (!cancelled) setTeamData({ leaderboard, unassigned })
      } finally {
        if (!cancelled) setIsTeamLoading(false)
      }
    }
    loadTeamData()

    return () => {
      cancelled = true
    }
  }, [isLoaded, canViewTargets, canViewAllLeads])

  const unassignedCount = stats.leadStats?.unassigned ?? 0

  // Team KPI band — one tile per granted source, so an ungranted metric never
  // renders as a null-safe zero.
  const kpiItems = useMemo<StatsMetalineItem[]>(() => {
    const skeletonValue = <Skeleton className="h-[13px] w-12" />
    const items: StatsMetalineItem[] = []

    if (canViewLeads) {
      items.push({
        key: "newLeads",
        label: t("teamKpi.newLeads"),
        value: isStatsLoading ? skeletonValue : (stats.leadStats?.this_week ?? 0),
        sub: isStatsLoading ? undefined : t("teamKpi.newLeadsHint"),
      })
    }

    if (canViewAllLeads) {
      items.push({
        key: "unassigned",
        label: t("teamKpi.unassigned"),
        value: isStatsLoading ? skeletonValue : unassignedCount,
        // Amber, not red: an unowned lead is a nudge, not a failure.
        accent: !isStatsLoading && unassignedCount > 0 ? "warning" : "default",
        sub: isStatsLoading
          ? undefined
          : unassignedCount > 0
            ? t("teamKpi.unassignedHint")
            : t("teamKpi.unassignedHintClear"),
      })
    }

    if (canViewQuotes) {
      items.push({
        key: "quotesSent",
        label: t("teamKpi.quotesSent"),
        value: isStatsLoading ? skeletonValue : (stats.quoteStats?.by_status?.sent ?? 0),
        sub: isStatsLoading
          ? undefined
          : t("teamKpi.quotesSentHint", { count: stats.quoteStats?.expiring_soon ?? 0 }),
      })
    }

    if (canViewAppointments) {
      items.push({
        key: "appointments",
        label: t("teamKpi.appointments"),
        value: isStatsLoading ? skeletonValue : (stats.appointmentStats?.this_week ?? 0),
        sub: isStatsLoading
          ? undefined
          : t("teamKpi.appointmentsHint", {
              today: stats.appointmentStats?.today ?? 0,
              upcoming: stats.appointmentStats?.upcoming ?? 0,
            }),
      })
    }

    return items
  }, [
    t,
    isStatsLoading,
    stats,
    unassignedCount,
    canViewLeads,
    canViewAllLeads,
    canViewQuotes,
    canViewAppointments,
  ])

  // Row 2 — the queue reads first (it is the actionable one), then the two
  // distribution charts. The grid collapses to however many survived gating.
  const workRow = useMemo(() => {
    const nodes: React.ReactNode[] = []

    if (canViewAllLeads) {
      nodes.push(
        <UnassignedLeadsQueue
          key="unassigned"
          data={teamData.unassigned}
          isLoading={isTeamLoading}
        />,
      )
    }
    if (canViewLeads && showLeadsStatusChart) {
      nodes.push(
        <LeadsStatusChart key="leads" data={stats.leadStats} isLoading={isStatsLoading} />,
      )
    }
    if (canViewAppointments) {
      nodes.push(
        <AppointmentsStatusChart
          key="appointments"
          data={stats.appointmentStats}
          isLoading={isStatsLoading}
        />,
      )
    }

    return nodes
  }, [
    canViewAllLeads,
    canViewLeads,
    canViewAppointments,
    showLeadsStatusChart,
    teamData.unassigned,
    isTeamLoading,
    stats,
    isStatsLoading,
  ])

  return (
    <ApolloPageShell>
      <ApolloPageHeader
        eyebrow={t("page.eyebrow")}
        title={t("variants.teamLeader.title")}
        stats={kpiItems.length > 0 ? <StatsMetaline items={kpiItems} /> : undefined}
      />

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* My Tasks — self-gating (tasks_enabled + tasks.view.assigned). */}
          <MyTasksWidget />

          {/* Row 1 — per-agent performance hero + the ranked strip beside it.
              Both render from the SAME `/leaderboard` response. On a clinic
              that trims the hero away the strip keeps the row to itself. */}
          {canViewTargets && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
              {showAgentPerformance && (
                <AgentPerformanceTable
                  className="lg:col-span-2"
                  data={teamData.leaderboard}
                  isLoading={isTeamLoading}
                />
              )}
              <div className={cn(showAgentPerformance ? undefined : "lg:col-span-3")}>
                <LeaderboardWidget data={teamData.leaderboard} isLoading={isTeamLoading} />
              </div>
            </div>
          )}

          {/* Row 2 — unassigned queue + distribution charts. */}
          {workRow.length > 0 && (
            <div
              className={cn(
                "grid gap-4 md:gap-6",
                WORK_GRID_COLS[workRow.length] ?? WORK_GRID_COLS[3],
              )}
            >
              {workRow}
            </div>
          )}
        </div>
      </div>
    </ApolloPageShell>
  )
}
